import type * as THREE from 'three';
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit';
import { addNonSelectableObject3DToParent } from '../../../utils/scene-object/addObject3DToParent';
import { ImportedModelInstancedSelectionBatch } from './ImportedModelInstancedSelectionBatch';

/**
 * 按正式 Unit 缓存选择批次。完整选择每 Unit 最多一批；精确子集每 primitive 最多一批，
 * 不按任意选择组合建立缓存，因此缓存上界由正式渲染结构决定，与点击次数无关。
 */
export class ImportedModelInstancedSelectionBatchManager {
    /** Unit 到完整/primitive 批次，-1 为完整几何。 */
    private readonly batchesByUnit = new Map<
        ImportedModelInstancedMeshUnit,
        Map<number, ImportedModelInstancedSelectionBatch>
    >();
    /** 正式 Unit 释放后同时移除缓存的强引用。 */
    private readonly unitSubscriptions = new Map<ImportedModelInstancedMeshUnit, () => void>();

    /**
     * @param resolveUnit source 的正式渲染批次查询。
     * @param getMaterial 当前选择配方。
     * @param resolveParent 选择显示组入口，必须保持正式父级坐标。
     * @param onCacheReleased 缓存释放后清理空分组，不触发渲染。
     */
    public constructor(
        private readonly resolveUnit: (source: THREE.Mesh) => ImportedModelInstancedMeshUnit | null,
        private readonly getMaterial: () => THREE.Material,
        private readonly resolveParent: (parent: THREE.Object3D) => THREE.Object3D,
        private readonly onCacheReleased: () => void,
    ) {}

    /**
     * 按 Unit 和固定 primitive 聚合选择，更新已有批次的槽遮罩。
     * @param sources 当前去重业务来源。
     * @param primitives 每个来源的精确 group 子集。
     */
    public apply(
        sources: readonly THREE.Mesh[],
        primitives?: ReadonlyMap<THREE.Mesh, readonly number[] | undefined>,
    ): void {
        const requests = new Map<ImportedModelInstancedMeshUnit, Map<number, Set<THREE.Mesh>>>();
        for (const source of sources) {
            const unit = this.resolveUnit(source);
            if (!unit) continue;
            let groups = requests.get(unit);
            if (!groups) {
                groups = new Map();
                requests.set(unit, groups);
            }
            const indices = primitives?.get(source);
            const groupCount = unit.object.geometry.groups.length;
            const keys = indices?.length && groupCount > 0 && indices.length < groupCount ? indices : [-1];
            for (const key of keys) {
                if (!Number.isInteger(key) || key < -1 || key >= groupCount)
                    throw new Error(`选择批次 primitive 越界：${key}`);
                let members = groups.get(key);
                if (!members) {
                    members = new Set();
                    groups.set(key, members);
                }
                members.add(source);
            }
        }
        try {
            for (const [unit, groups] of requests) {
                for (const [key, members] of groups) this.ensureBatch(unit, key).setSelection(members);
            }
            for (const [unit, batches] of this.batchesByUnit) {
                for (const [key, batch] of batches) if (!requests.get(unit)?.has(key)) batch.clearSelection();
            }
        } catch (error) {
            this.dispose();
            throw error;
        }
    }

    /** 清空选择但保留有界的热态 GPU 缓存，不能以 dispose 代替普通取消。 */
    public clear(): void {
        for (const batches of this.batchesByUnit.values())
            for (const batch of batches.values()) batch.clearSelection();
    }

    /**
     * 为一个 Unit 准备真实管线预热；未选槽的遮罩仍全部关闭，因此不会写入颜色或深度。
     * @param unit 已挂载的正式 Unit。
     * @returns 必须在本帧 finally 中调用的显示恢复函数；不改变任何选择槽。
     */
    public prepareWarmup(unit: ImportedModelInstancedMeshUnit): () => void {
        const batch = this.ensureBatch(unit, -1);
        const parent = batch.parent;
        const parentVisible = parent?.visible;
        batch.visible = true;
        if (parent) parent.visible = true;
        return () => {
            if (!batch.isDisposed) batch.visible = batch.selectedCount > 0;
            if (parent) parent.visible = parentVisible ?? false;
        };
    }

    /** @param unit 已撤销发布的正式 Unit；立即清除缓存，不能等到下一次选择。 */
    public removeUnit(unit: ImportedModelInstancedMeshUnit): void {
        if (this.batchesByUnit.has(unit)) this.releaseUnit(unit);
    }

    /** 释放全部缓存和 Unit 订阅；由 Viewer/视觉状态销毁入口调用。 */
    public dispose(): void {
        const errors: unknown[] = [];
        for (const unit of [...this.batchesByUnit.keys()]) {
            try {
                this.releaseUnit(unit);
            } catch (error) {
                errors.push(error);
            }
        }
        if (errors.length) throw new AggregateError(errors, '选择批次缓存释放失败');
    }

    /** @param unit 正式 Unit。 @param key 固定 primitive 或完整范围。 @returns 可复用的选择批次。 */
    private ensureBatch(
        unit: ImportedModelInstancedMeshUnit,
        key: number,
    ): ImportedModelInstancedSelectionBatch {
        const parent = unit.object.parent;
        if (!parent) throw new Error('选择批次缺少正式实例父级');
        let batches = this.batchesByUnit.get(unit);
        if (!batches) {
            batches = new Map();
            this.batchesByUnit.set(unit, batches);
            this.unitSubscriptions.set(
                unit,
                unit.onDispose(() => this.releaseUnit(unit)),
            );
        }
        let batch = batches.get(key);
        if (!batch || batch.isDisposed) {
            batch = new ImportedModelInstancedSelectionBatch(unit, this.getMaterial(), key);
            batches.set(key, batch);
        }
        const selectionParent = this.resolveParent(parent);
        if (batch.parent !== selectionParent) {
            addNonSelectableObject3DToParent(batch, selectionParent);
            // 仅首次挂载/迁移时刷新父空间，适配关闭场景自动矩阵更新的 SDK 宿主。
            batch.matrixWorldNeedsUpdate = true;
            batch.updateWorldMatrix(true, false);
        }
        return batch;
    }

    /** @param unit 已销毁/重建或结束 Viewer 生命周期的正式批次。 */
    private releaseUnit(unit: ImportedModelInstancedMeshUnit): void {
        this.unitSubscriptions.get(unit)?.();
        this.unitSubscriptions.delete(unit);
        const batches = this.batchesByUnit.get(unit);
        this.batchesByUnit.delete(unit);
        const errors: unknown[] = [];
        if (batches)
            for (const batch of batches.values()) {
                try {
                    batch.dispose();
                } catch (error) {
                    errors.push(error);
                }
            }
        this.onCacheReleased();
        if (errors.length) throw new AggregateError(errors, '正式 Unit 的选择缓存释放失败');
    }
}
